import type { Logger } from "@expert/logging";

import ky, { HTTPError } from "ky";

import type { DefaultPayload } from "./types";

import { defaultHeaders, defaultTimeoutInMS } from "../types";

/**
 * No MDN: the line comes from `token`, the one `verifyVerizonCustomer` returned once the PIN was verified, or
 * `authorizeVerizonInAcss` once the expert authorized the customer in ACSS.
 */
type PrepareVerizonReplacementPayload = DefaultPayload & {
    token: string;
};

/**
 * Why the backend found no Verizon replacement device it can activate. `LINE_MISMATCH` means the shipped
 * claim is for a different line on the account than the verified MDN; like AT&T, only the verified line
 * is activated. Every code routes to the guide's `noEligibleClaim` step, the manual flow in ACSS.
 */
export type VerizonIneligibleCode = "NO_ELIGIBLE_CLAIM" | "MISSING_DEVICE_DETAILS" | "LINE_MISMATCH";

/**
 * The replacement device as described to the expert. The backend never returns the IMEI, ICCID or the
 * full line: they stay inside `token`, which `activateDevice` sends back.
 */
export interface VerizonReplacementDevice {
    make: string;
    model: string;
    eSim: boolean;
    lineLast4: string;
    claimDate: string;
    /**
     * Set only for a device the expert typed in (`submitTypedVerizonDevice`), which has no claim: no make, model or
     * claim date, just the last 4 of the IMEI they entered.
     */
    typedImeiLast4?: string;
}

/** A device the expert can pick, with the token that activates it and no other. */
export type VerizonReplacementOption = VerizonReplacementDevice & { token: string };

/**
 * `devices` lists every device the backend can activate for the line, newest claim first, for the expert to pick
 * from. The top-level device is the newest, and the only one a backend that predates the list returns.
 */
export type PrepareVerizonReplacementResult =
    | ({ eligible: true; devices?: VerizonReplacementOption[] } & VerizonReplacementOption)
    | { eligible: false; code?: VerizonIneligibleCode; errorCode?: string };

/**
 * Looks up the Verizon replacement devices for the verified line (PIN, or authorized in ACSS). A request that
 * fails (403 when the expert isn't enabled or the verification token is invalid, 400, 500, network)
 * resolves to `eligible: false` with no `code`, so the guide falls back to the manual flow.
 */
export const prepareVerizonReplacement = async (
    ssoAccessToken: string,
    payload: PrepareVerizonReplacementPayload,
    logger: Logger,
    apiBaseUrl: string,
): Promise<PrepareVerizonReplacementResult> => {
    try {
        const url = `${apiBaseUrl}/api/device-activation/v1/verizon/replacement`;
        const resp = await ky.post(url, {
            headers: {
                ...defaultHeaders,
                Authorization: `Bearer ${ssoAccessToken}`,
            },
            json: payload,
            timeout: defaultTimeoutInMS,
        });

        return await resp.json<PrepareVerizonReplacementResult>();
    } catch (error) {
        logger.warn({ error }, "Solve Guide | Error: Could not look up the Verizon replacement device.");

        let errorCode = "";
        if (error instanceof HTTPError) {
            try {
                const errorJson = await error.response.json<{ code?: string }>();
                errorCode = errorJson.code ?? "";
            } catch {
                errorCode = "";
            }
        }

        return { eligible: false, errorCode };
    }
};
